import React, { useState, useEffect } from 'react';
import orderService from '../services/orderService';
import { getSizeLabel } from '../utils/constants';

const STATUS_OPTIONS = ['PENDING', 'CONFIRMED', 'PROCESSING', 'SHIPPED', 'DELIVERED', 'CANCELLED', 'REFUNDED'];
const SUPPLIER_OPTIONS = ['', 'PRINTROVE', 'QIKINK'];

const STATUS_COLORS = {
  PENDING:    { background: '#fefcbf', color: '#744210' },
  CONFIRMED:  { background: '#bee3f8', color: '#2a4365' },
  PROCESSING: { background: '#e9d8fd', color: '#44337a' },
  SHIPPED:    { background: '#c3dafe', color: '#2a3a8c' },
  DELIVERED:  { background: '#c6f6d5', color: '#22543d' },
  CANCELLED:  { background: '#fed7d7', color: '#822727' },
  REFUNDED:   { background: '#e2e8f0', color: '#2d3748' },
};

const fmt = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

const AdminOrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null); // full order from API
  const [editLoading, setEditLoading] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null); // { type: 'success'|'error', text }

  // Form state — only the editable fields
  const [form, setForm] = useState({
    status: '',
    courierName: '',
    trackingNumber: '',
    supplierName: '',
    supplierOrderId: '',
    adminNotes: '',
  });

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const data = await orderService.adminGetAllOrders();
      setOrders(data);
    } catch (err) {
      console.error('Failed to load orders', err);
      alert('Could not load orders. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const openEdit = async (orderId) => {
    setEditLoading(true);
    setSaveMsg(null);
    try {
      const order = await orderService.adminGetOrder(orderId);
      setSelectedOrder(order);
      setForm({
        status:          order.status        || '',
        courierName:     order.courierName   || '',
        trackingNumber:  order.trackingNumber|| '',
        supplierName:    order.supplierName  || '',
        supplierOrderId: order.supplierOrderId || '',
        adminNotes:      order.adminNotes    || '',
      });
    } catch (err) {
      console.error('Failed to load order detail', err);
      alert('Could not load order details.');
    } finally {
      setEditLoading(false);
    }
  };

  const closeEdit = () => {
    setSelectedOrder(null);
    setSaveMsg(null);
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    setSaveMsg(null);
    try {
      // Only send non-empty strings so the backend null-check skips unchanged fields
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== '') payload[k] = v;
      });

      const updated = await orderService.adminUpdateOrder(selectedOrder.id, payload);
      setSelectedOrder(updated);
      setSaveMsg({ type: 'success', text: 'Order updated successfully.' });
      // Refresh the table row in place
      setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
    } catch (err) {
      console.error('Save failed', err);
      setSaveMsg({ type: 'error', text: err.response?.data?.message || 'Save failed.' });
    }
  };

  // ─── Edit Panel ─────────────────────────────────────────────────────────────
  if (editLoading) {
    return <div style={styles.container}><p style={{ color: '#718096' }}>Loading order details…</p></div>;
  }

  if (selectedOrder) {
    const o = selectedOrder;
    return (
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <button style={styles.backBtn} onClick={closeEdit}>← Back to Orders</button>
            <h1 style={styles.title}>Order {o.orderNumber}</h1>
            <p style={{ margin: '4px 0 0', color: '#718096', fontSize: '0.9rem' }}>
              Placed {fmt(o.createdAt)} · {o.customerName} · {o.customerEmail}
            </p>
          </div>
          <span style={{ ...styles.statusBadge, ...STATUS_COLORS[o.status] }}>{o.status}</span>
        </div>
        <hr style={styles.divider} />

        <div style={styles.twoCol}>
          {/* ── Left: read-only order info ── */}
          <div>
            <h2 style={styles.sectionTitle}>Order Items</h2>
            <div style={styles.card}>
              {o.items?.map((item) => (
                <div key={item.id} style={styles.itemRow}>
                  {item.productImage && (
                    <img src={item.productImage} alt={item.productName} style={styles.itemImg} />
                  )}
                  <div>
                    <div style={{ fontWeight: 600 }}>{item.productName}</div>
                    <div style={{ fontSize: '0.85rem', color: '#718096' }}>
                      Size: {getSizeLabel(item.size)} · Qty: {item.quantity}
                    </div>
                    {(item.customName || item.passingYear) && (
                      <div style={{ fontSize: '0.85rem', color: '#2b6cb0' }}>
                        {item.customName
                          ? `Print name: ${item.customName}${item.passingYear ? ` · ${item.passingYear}` : ''}`
                          : `Passing year: ${item.passingYear}`}
                      </div>
                    )}
                    {item.designGenerated && item.designUrl && (
                      <a
                        href={item.designUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: '0.85rem', color: '#2b6cb0' }}
                      >
                        View generated design
                      </a>
                    )}
                  </div>
                  <div style={{ marginLeft: 'auto', fontWeight: 600 }}>₹{item.subtotal}</div>
                </div>
              ))}
              <div style={styles.totalRow}>
                <span>Subtotal</span><span>₹{o.subtotal}</span>
              </div>
              <div style={styles.totalRow}>
                <span>Shipping</span><span>₹{o.shippingCharge}</span>
              </div>
              <div style={{ ...styles.totalRow, ...(Math.abs((Number(o.subtotal) + Number(o.shippingCharge)) - Number(o.totalAmount)) < 0.5 ? { color: '#64748b', fontSize: '0.85rem' } : {}) }}>
                <span>{Math.abs((Number(o.subtotal) + Number(o.shippingCharge)) - Number(o.totalAmount)) < 0.5 ? 'Includes GST' : 'GST'}</span><span>₹{o.tax}</span>
              </div>
              <div style={{ ...styles.totalRow, fontWeight: 700, fontSize: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: 10, marginTop: 4 }}>
                <span>Total</span><span>₹{o.totalAmount}</span>
              </div>
            </div>

            <h2 style={{ ...styles.sectionTitle, marginTop: 24 }}>Delivery Address</h2>
            <div style={styles.card}>
              <p style={styles.addrLine}><strong>{o.deliveryAddress?.fullName}</strong> · {o.deliveryAddress?.phone}</p>
              <p style={styles.addrLine}>{o.deliveryAddress?.addressLine1}</p>
              {o.deliveryAddress?.addressLine2 && <p style={styles.addrLine}>{o.deliveryAddress.addressLine2}</p>}
              <p style={styles.addrLine}>{o.deliveryAddress?.city}, {o.deliveryAddress?.state} – {o.deliveryAddress?.pincode}</p>
            </div>

            <h2 style={{ ...styles.sectionTitle, marginTop: 24 }}>Payment</h2>
            <div style={styles.card}>
              <p style={styles.addrLine}>Method: <strong>{o.paymentMethod}</strong></p>
              <p style={styles.addrLine}>Paid: <strong>{o.paymentCompleted ? 'Yes' : 'No'}</strong></p>
              {o.refundStatus && (
                <>
                  <p style={styles.addrLine}>Refund Status: <strong>{o.refundStatus}</strong></p>
                  {o.refundId && (
                    <p style={styles.addrLine}>Refund ID: <span style={{ fontFamily: 'monospace' }}>{o.refundId}</span></p>
                  )}
                  {o.refundedAt && <p style={styles.addrLine}>Refunded At: {fmt(o.refundedAt)}</p>}
                </>
              )}
            </div>

            {o.notes && (
              <>
                <h2 style={{ ...styles.sectionTitle, marginTop: 24 }}>Customer Notes</h2>
                <div style={{ ...styles.card, background: '#fffbeb', borderColor: '#fbd38d' }}>
                  <p style={{ margin: 0 }}>{o.notes}</p>
                </div>
              </>
            )}
          </div>

          {/* ── Right: editable fields ── */}
          <div>
            <h2 style={styles.sectionTitle}>Update Order</h2>
            <div style={styles.card}>
              {saveMsg && (
                <div style={{
                  padding: '10px 14px',
                  borderRadius: 6,
                  marginBottom: 16,
                  fontSize: '0.9rem',
                  background: saveMsg.type === 'success' ? '#c6f6d5' : '#fed7d7',
                  color:      saveMsg.type === 'success' ? '#22543d' : '#822727',
                }}>
                  {saveMsg.text}
                </div>
              )}

              <label style={styles.label}>Status</label>
              <select name="status" value={form.status} onChange={handleChange} style={styles.input}>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>

              <label style={styles.label}>Courier Name</label>
              <input name="courierName" value={form.courierName} onChange={handleChange}
                placeholder="e.g. Delhivery, Blue Dart" style={styles.input} />

              <label style={styles.label}>Tracking Number</label>
              <input name="trackingNumber" value={form.trackingNumber} onChange={handleChange}
                placeholder="Courier tracking number" style={styles.input} />

              <hr style={{ ...styles.divider, margin: '20px 0' }} />

              <p style={{ margin: '0 0 12px', fontWeight: 600, fontSize: '0.85rem', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Supplier Info (Internal)
              </p>

              <label style={styles.label}>Supplier</label>
              <select name="supplierName" value={form.supplierName} onChange={handleChange} style={styles.input}>
                {SUPPLIER_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s || '— None —'}</option>
                ))}
              </select>

              <label style={styles.label}>Supplier Order ID</label>
              <input name="supplierOrderId" value={form.supplierOrderId} onChange={handleChange}
                placeholder="e.g. 707566 (from Printrove/Qikink)" style={styles.input} />

              <label style={styles.label}>Admin Notes</label>
              <textarea name="adminNotes" value={form.adminNotes} onChange={handleChange}
                placeholder="Internal notes — not visible to customer"
                rows={4} style={{ ...styles.input, resize: 'vertical' }} />

              <button onClick={handleSave} style={styles.saveBtn}>Save Changes</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Orders Table ────────────────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <button style={styles.backBtn} onClick={() => window.location.href = '/admin/dashboard'}>
            ← Back to Dashboard
          </button>
          <h1 style={styles.title}>Orders</h1>
        </div>
        <button style={styles.refreshBtn} onClick={fetchOrders} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <hr style={styles.divider} />

      {loading ? (
        <div style={styles.placeholder}><p>Loading orders…</p></div>
      ) : orders.length === 0 ? (
        <div style={styles.placeholder}>
          <div style={{ fontSize: '3rem', marginBottom: 12 }}>📦</div>
          <h3>No orders yet</h3>
        </div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.thRow}>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Order #</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Supplier</th>
                <th style={styles.th}>Supplier Order ID</th>
                <th style={styles.th}>Total</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} style={styles.tr}>
                  <td style={styles.td}>{fmt(o.createdAt)}</td>
                  <td style={{ ...styles.td, fontFamily: 'monospace', fontWeight: 600 }}>{o.orderNumber}</td>
                  <td style={styles.td}>
                    <div>{o.customerName}</div>
                    <div style={{ fontSize: '0.8rem', color: '#718096' }}>{o.customerEmail}</div>
                  </td>
                  <td style={styles.td}>
                    <span style={{ ...styles.statusBadge, ...STATUS_COLORS[o.status] }}>{o.status}</span>
                    {o.refundStatus && (
                      <div style={{ marginTop: 4, fontSize: '0.72rem', color: o.refundStatus === 'processed' ? '#22543d' : '#2b6cb0' }}>
                        ↩ Refund {o.refundStatus}
                      </div>
                    )}
                  </td>
                  <td style={styles.td}>{o.supplierName || <span style={{ color: '#a0aec0' }}>—</span>}</td>
                  <td style={{ ...styles.td, fontFamily: 'monospace' }}>
                    {o.supplierOrderId || <span style={{ color: '#a0aec0' }}>—</span>}
                  </td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>₹{o.totalAmount}</td>
                  <td style={styles.td}>
                    <button style={styles.editBtn} onClick={() => openEdit(o.id)}>
                      View / Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const styles = {
  container:    { padding: '40px', maxWidth: '1300px', margin: '0 auto', fontFamily: 'Segoe UI, sans-serif', color: '#2d3748' },
  header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' },
  title:        { margin: 0, fontSize: '1.8rem', color: '#111' },
  backBtn:      { background: 'none', border: 'none', color: '#2b6cb0', cursor: 'pointer', padding: 0, fontSize: '0.9rem', marginBottom: 8, textDecoration: 'underline' },
  refreshBtn:   { backgroundColor: '#2b6cb0', color: 'white', padding: '8px 18px', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 },
  divider:      { border: 0, height: 1, background: '#e2e8f0', margin: '20px 0 28px' },
  placeholder:  { textAlign: 'center', padding: '60px 20px', border: '2px dashed #e2e8f0', borderRadius: 12, color: '#718096' },
  tableWrapper: { overflowX: 'auto', background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  table:        { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
  thRow:        { background: '#f7fafc', borderBottom: '2px solid #edf2f7' },
  th:           { padding: '14px 16px', color: '#4a5568', fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap' },
  tr:           { borderBottom: '1px solid #edf2f7' },
  td:           { padding: '14px 16px', fontSize: '0.92rem', verticalAlign: 'middle' },
  statusBadge:  { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 700 },
  editBtn:      { background: '#ecc94b', color: '#744210', border: 'none', padding: '6px 14px', borderRadius: 4, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' },

  // Edit panel
  twoCol:       { display: 'grid', gridTemplateColumns: '1fr 380px', gap: 32, alignItems: 'start' },
  sectionTitle: { fontSize: '1rem', fontWeight: 700, color: '#2d3748', margin: '0 0 10px' },
  card:         { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '20px' },
  itemRow:      { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 },
  itemImg:      { width: 48, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' },
  totalRow:     { display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: '#4a5568', marginTop: 6 },
  addrLine:     { margin: '0 0 4px', fontSize: '0.92rem' },
  label:        { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#4a5568', marginBottom: 4, marginTop: 14 },
  input:        { width: '100%', padding: '8px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box', outline: 'none' },
  saveBtn:      { marginTop: 20, width: '100%', backgroundColor: '#2b6cb0', color: 'white', padding: '10px', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '0.95rem' },
};

export default AdminOrderManagement;
